/**
 * Memoized greedy-mesh results per chunk, recomputed only when the chunk's
 * tile data actually changed (_docs/tilemap/05 §7). Rides the same
 * `renderDirty` flag the ChunkRenderer consumes: the caller captures it
 * *before* asking the renderer to sync the chunk (which clears the flag), so
 * one dirty chunk costs one re-mesh, exactly like the bitmap rebuild it rides
 * alongside.
 */

import { greedyMeshChunk, type TileRect } from '../model/greedyMesh';
import type { Chunk } from '../model/chunk';

export class CollisionCache {
    private cache = new Map<string, TileRect[]>();

    private key(layerUuid: string, cx: number, cy: number): string {
        return `${layerUuid}:${cx},${cy}`;
    }

    /**
     * Rects for one chunk, recomputed only if `wasDirty` (captured by the
     * caller before the chunk's `renderDirty` flag gets cleared elsewhere).
     */
    rectsFor(layerUuid: string, chunk: Chunk, wasDirty: boolean, isSolid: (id: number) => boolean): TileRect[] {
        const key = this.key(layerUuid, chunk.cx, chunk.cy);
        if (!wasDirty) {
            const cached = this.cache.get(key);
            if (cached) return cached;
        }
        const rects = greedyMeshChunk(chunk.tiles, isSolid);
        this.cache.set(key, rects);
        return rects;
    }

    invalidate(layerUuid: string, cx: number, cy: number): void {
        this.cache.delete(this.key(layerUuid, cx, cy));
    }

    clear(): void {
        this.cache.clear();
    }
}
